// Recepción de rosas (almacén, en ordenador): SOLO las líneas del envío que cubren pedidos rosas, con la misma
// asignación que la hoja de recepción. Cada línea: OK / falta / dañado-incorrecto (+ nota), con usuario y hora.
// Atajos: ↑/↓ (o J/K) moverse · 1/O OK · 2/F falta · 3/D dañado · N nota · B lector · T terminar y avisar.
// Se llega desde la hoja (/recepcion/<proveedor>/<factura>) o desde Envíos de proveedor.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ClipboardCheck, FileText, Keyboard, Loader2, PackageOpen,
  ScanBarcode, Send, Undo2,
} from 'lucide-react'
import {
  EtiquetaPedido, RESULTADO_ESTILO, Recorrido, SinPermiso, fmtFecha, nombreCorto, useAccesoRecepcion, type EnvioDetalle,
  type LineaRosa, type Resultado,
} from '@/components/recepcion/comun'

const eur = (v: number | null) => (v == null ? '' : v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }))

// Nº de pedido (PS con enlace / Factusol) y cliente de una línea rosa; las manuales sin pedido, «a mano #N»
function PedidoCliente({ l }: { l: LineaRosa }) {
  const numero = l.id_order ?? null
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        {numero && l.origen_pedido ? (
          <EtiquetaPedido numero={numero} origen={l.origen_pedido} size="sm" />
        ) : (
          <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">{l.pedido}</span>
        )}
        {l.dias != null && (
          <span className={`text-xs ${l.dias > 7 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-500 dark:text-slate-400'}`}
            title={l.fecha_pedido ? `Esperando desde el ${fmtFecha(l.fecha_pedido)}` : undefined}>{l.dias} d</span>
        )}
      </div>
      {l.cliente && <div className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate max-w-[12rem]" title={l.cliente}>{l.cliente}</div>}
    </div>
  )
}

const norm = (c?: string | null) => {
  const s = (c || '').replace(/\s+/g, '').toUpperCase()
  return s.startsWith('1-') ? s.slice(2) : s
}

const errorTexto = (e: unknown) => String((e as any)?.response?.data?.detail || (e as any)?.message || e)

// /recepcion/:proveedor/:documento (enlace de la hoja) -> /logistics/supplier-shipments/:id/recepcion
export function ReceptionLinkRedirect() {
  const { proveedor, documento } = useParams()
  const navigate = useNavigate()
  const acceso = useAccesoRecepcion()
  const q = useQuery({
    queryKey: ['recepcion-por-documento', proveedor, documento],
    queryFn: async () => (await api.get<{ id: number }>(`/recepcion-rosas/por-documento/${proveedor}/${documento}`)).data,
    enabled: acceso,
    retry: false,
  })
  useEffect(() => {
    if (q.data) navigate(`/logistics/supplier-shipments/${q.data.id}/recepcion`, { replace: true })
  }, [q.data, navigate])
  if (!acceso) return <SinPermiso />
  if (q.isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertTriangle className="w-8 h-8 text-amber-500" />
        <p className="text-slate-700 dark:text-slate-300">No se encuentra el envío de {proveedor} con la factura {documento}.</p>
        <Link to="/logistics/supplier-shipments" className="text-indigo-600 hover:underline">Ver todos los envíos</Link>
      </div>
    )
  }
  return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
}

export default function ShipmentReceptionPage() {
  const { id } = useParams()
  const acceso = useAccesoRecepcion()
  const qc = useQueryClient()
  const key = ['recepcion-envio', id]
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<EnvioDetalle>(`/recepcion-rosas/envios/${id}`)).data,
    enabled: acceso && !!id,
  })
  const [sel, setSel] = useState(0)
  const [notas, setNotas] = useState<Record<string, string>>({})
  const [notaAbierta, setNotaAbierta] = useState<string | null>(null)
  const [codigo, setCodigo] = useState('')
  const [okAlLeer, setOkAlLeer] = useState(true)
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'error' | 'info'; texto: string } | null>(null)
  const [verTodo, setVerTodo] = useState(false)
  const lectorRef = useRef<HTMLInputElement>(null)
  const notaRef = useRef<HTMLInputElement>(null)
  const filasRef = useRef<(HTMLTableRowElement | null)[]>([])
  const notaAbiertaRef = useRef<string | null>(null)
  notaAbiertaRef.current = notaAbierta

  const lineas: LineaRosa[] = useMemo(() => q.data?.lineas_rosas || [], [q.data])
  const pendientes = lineas.filter((l) => !l.resultado).length
  const actual = lineas[Math.min(sel, Math.max(lineas.length - 1, 0))]

  const actualizar = (d: EnvioDetalle) => {
    qc.setQueryData(key, d)
    qc.invalidateQueries({ queryKey: ['recepcion-envios'] })
  }
  const marcar = useMutation({
    mutationFn: async ({ l, resultado, nota }: { l: LineaRosa; resultado: Resultado; nota?: string | null }) =>
      (await api.post<{ envio: EnvioDetalle; rosas_verificados: boolean }>(`/recepcion-rosas/envios/${id}/lineas`,
        { marcas: [{ linea: l.linea, resultado, nota: nota ?? notas[l.linea] ?? l.nota ?? null }] })).data,
    onSuccess: (r, v) => {
      actualizar(r.envio)
      setAviso({ tipo: 'ok', texto: `${v.l.referencia} · pedido ${v.l.pedido}: ${RESULTADO_ESTILO[v.resultado].texto}` +
        (r.rosas_verificados ? ' · todas las líneas rosas verificadas' : '') })
      // siguiente línea sin marcar
      const idx = r.envio.lineas_rosas.findIndex((x) => x.linea === v.l.linea)
      const sig = r.envio.lineas_rosas.findIndex((x, i) => i > idx && !x.resultado)
      const primera = r.envio.lineas_rosas.findIndex((x) => !x.resultado)
      setSel(sig >= 0 ? sig : primera >= 0 ? primera : Math.max(idx, 0))
    },
    onError: (e) => setAviso({ tipo: 'error', texto: errorTexto(e) }),
  })
  const llegado = useMutation({
    mutationFn: async () => (await api.post<EnvioDetalle>(`/recepcion-rosas/envios/${id}/llegado`)).data,
    onSuccess: (d) => { actualizar(d); setAviso({ tipo: 'ok', texto: 'Bulto recibido: guardado con tu usuario y la hora' }) },
    onError: (e) => setAviso({ tipo: 'error', texto: errorTexto(e) }),
  })
  const terminar = useMutation({
    mutationFn: async () => (await api.post<{ enviado: boolean; lineas: number }>(`/recepcion-rosas/envios/${id}/terminar`)).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: key })
      setAviso({ tipo: 'ok', texto: r.enviado ? `Resumen enviado a comercial (${r.lineas} línea${r.lineas === 1 ? '' : 's'})` : 'No hay marcas nuevas que avisar' })
    },
    onError: (e) => setAviso({ tipo: 'error', texto: errorTexto(e) }),
  })
  const revisado = useMutation({
    mutationFn: async () => (await api.post<EnvioDetalle>(`/recepcion-rosas/envios/${id}/revisado`, {})).data,
    onSuccess: (d) => { actualizar(d); setAviso({ tipo: 'ok', texto: 'Envío marcado como revisado completo' }) },
    onError: (e) => setAviso({ tipo: 'error', texto: errorTexto(e) }),
  })
  const deshacer = useMutation({
    mutationFn: async (paso: string) => (await api.post<EnvioDetalle>(`/recepcion-rosas/envios/${id}/deshacer`, { paso })).data,
    onSuccess: (d) => actualizar(d),
  })

  const leer = (valor: string) => {
    const c = norm(valor)
    if (!c) return
    const cand = lineas.map((l, i) => ({ l, i })).filter(({ l }) => norm(l.ref_proveedor) === c || norm(l.referencia) === c)
    if (!cand.length) {
      setAviso({ tipo: 'info', texto: `${valor}: no es de ningún pedido rosa de este envío (va con el resto del pedido)` })
      return
    }
    const elegida = cand.find(({ l }) => !l.resultado) || cand[0]
    setSel(elegida.i)
    filasRef.current[elegida.i]?.scrollIntoView({ block: 'center' })
    if (okAlLeer && !elegida.l.resultado) marcar.mutate({ l: elegida.l, resultado: 'ok' })
    else setAviso({ tipo: 'info', texto: `${valor}: línea ${elegida.i + 1} (pedido ${elegida.l.pedido})` })
  }

  // Atajos de teclado (fuera de los campos de texto)
  useEffect(() => {
    const h = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return
      // con la nota abierta (aún sin foco) o con otra ventana encima (p. ej. «Comprobación de stock», que tiene sus
      // propias teclas S/M/N), las teclas no son para esta pantalla
      if (notaAbiertaRef.current || document.querySelector('body > div.fixed.inset-0')) return
      if (ev.ctrlKey || ev.metaKey || ev.altKey || !lineas.length && !['b', 'B', '/'].includes(ev.key)) return
      const k = ev.key.toLowerCase()
      const mover = (d: number) => {
        const n = Math.min(Math.max(sel + d, 0), lineas.length - 1)
        setSel(n)
        filasRef.current[n]?.scrollIntoView({ block: 'nearest' })
      }
      if (k === 'arrowdown' || k === 'j') { ev.preventDefault(); mover(1) }
      else if (k === 'arrowup' || k === 'k') { ev.preventDefault(); mover(-1) }
      else if (actual && (k === '1' || k === 'o')) { ev.preventDefault(); marcar.mutate({ l: actual, resultado: 'ok' }) }
      else if (actual && (k === '2' || k === 'f')) { ev.preventDefault(); marcar.mutate({ l: actual, resultado: 'falta' }) }
      else if (actual && (k === '3' || k === 'd')) { ev.preventDefault(); marcar.mutate({ l: actual, resultado: 'danado' }) }
      else if (actual && k === 'n') { ev.preventDefault(); setNotaAbierta(actual.linea); setTimeout(() => notaRef.current?.focus(), 0) }
      else if (k === 'b' || k === '/') { ev.preventDefault(); lectorRef.current?.focus() }
      else if (k === 't') { ev.preventDefault(); terminar.mutate() }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [lineas, sel, actual, marcar, terminar])

  // al cargar: primera línea sin marcar
  useEffect(() => {
    const i = lineas.findIndex((l) => !l.resultado)
    if (i >= 0) setSel(i)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.id])

  if (!acceso) return <SinPermiso />
  if (q.isLoading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
  if (q.isError || !q.data) return <p className="text-center text-red-600 py-16">No se ha podido cargar el envío: {errorTexto(q.error)}</p>
  const e = q.data

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/logistics/supplier-shipments" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"><ArrowLeft className="w-4 h-4" /> Envíos de proveedor</Link>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mt-1 flex flex-wrap items-baseline gap-x-2">
            Recepción · {e.supplier_name}{e.factusol_order ? <span className="text-slate-700 dark:text-slate-200">1-{e.factusol_order}</span> : null}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
            <Recorrido envio={e} conTexto />
            <span>{e.proveedor === 'albaran' ? 'Albarán' : 'Factura'} {e.documento} · {fmtFecha(e.doc_date || e.facturado_at)}</span>
            <span>{e.n_lines} líneas, {e.units} uds{e.total ? ` · ${eur(e.total)}` : ''}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Solo las líneas que esperan clientes (pedidos rosas). El resto del pedido se revisa después, como siempre.</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!e.llegado_at ? (
              <button onClick={() => llegado.mutate()} disabled={llegado.isPending} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-500 dark:focus-visible:ring-offset-slate-900">
                {llegado.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageOpen className="w-4 h-4" />} Bulto recibido
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-amber-50 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
                <PackageOpen className="w-4 h-4" /> Recibido {fmtFecha(e.llegado_at)} · {nombreCorto(e.llegado_por_nombre)}
                <button onClick={() => { if (confirm('¿Quitar «bulto recibido»? (se quita también lo que depende de ello)')) deshacer.mutate('llegado') }} title="Deshacer «bulto recibido»" aria-label="Deshacer bulto recibido" className="ml-1 rounded text-amber-600 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"><Undo2 className="w-3.5 h-3.5" /></button>
              </span>
            )}
            <button onClick={() => terminar.mutate()} disabled={terminar.isPending || !e.pendiente_aviso} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-pink-500 dark:focus-visible:ring-offset-slate-900" title="Atajo: T">
              {terminar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Terminar y avisar a comercial{e.pendiente_aviso ? ` (${e.pendiente_aviso})` : ''}
            </button>
          </div>
          <div className="flex items-center gap-1 text-sm">
            {e.url_hoja && <a href={e.url_hoja} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"><FileText className="w-4 h-4" /> Hoja PDF</a>}
            {e.llegado_at && !e.revisado_at && (
              <button onClick={() => revisado.mutate()} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                <ClipboardCheck className="w-4 h-4" /> Revisado completo
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lector de códigos y estado */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
        <ScanBarcode className="w-5 h-5 text-slate-500" />
        <form onSubmit={(ev) => { ev.preventDefault(); leer(codigo); setCodigo('') }} className="flex items-center gap-2">
          <input ref={lectorRef} value={codigo} onChange={(ev) => setCodigo(ev.target.value)} placeholder="Lector de códigos (B)" aria-label="Lector de códigos"
            onKeyDown={(ev) => { if (ev.key === 'Escape') (ev.target as HTMLInputElement).blur() }}
            className="w-64 px-3 py-2 text-sm font-mono border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={okAlLeer} onChange={(ev) => setOkAlLeer(ev.target.checked)} /> marcar OK al leer
          </label>
        </form>
        <span className={`ml-auto text-sm font-medium ${pendientes ? 'text-pink-700 dark:text-pink-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
          {lineas.length === 0 ? 'Ningún pedido rosa espera piezas de este envío' : pendientes ? `${pendientes} de ${lineas.length} líneas por verificar` : `Las ${lineas.length} líneas rosas están verificadas`}
        </span>
      </div>
      {aviso && (
        <div role="status" className={`rounded-lg px-3 py-2 text-sm ${aviso.tipo === 'error' ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : aviso.tipo === 'ok' ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300'}`}>{aviso.texto}</div>
      )}

      {/* Líneas rosas */}
      {lineas.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-pink-200 dark:border-pink-900/60 bg-white dark:bg-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-pink-600 text-white text-left">
              <tr>
                <th className="px-3 py-2">#</th><th className="px-3 py-2">Referencia <span className="font-normal opacity-80">(proveedor)</span></th>
                <th className="px-3 py-2">Descripción</th><th className="px-3 py-2 text-center">Uds</th><th className="px-3 py-2">Pedido · cliente</th>
                <th className="px-3 py-2">Recepción</th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => {
                const activa = i === sel
                return (
                  <tr key={l.linea} ref={(el) => { filasRef.current[i] = el }} data-linea={l.linea}
                    onClick={() => { setSel(i); if (notaAbierta && notaAbierta !== l.linea) setNotaAbierta(null) }}
                    className={`border-t border-pink-100 dark:border-pink-900/40 cursor-pointer ${activa ? 'bg-indigo-50 dark:bg-indigo-950/40 outline outline-2 outline-indigo-400' : l.resultado ? 'bg-slate-50/60 dark:bg-slate-900/30' : 'bg-pink-50/50 dark:bg-pink-950/20'}`}>
                    <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-mono font-semibold text-slate-900 dark:text-white">{l.referencia}</div>
                      <div className="font-mono text-xs text-slate-500 dark:text-slate-400">{l.ref_proveedor}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200 max-w-[13rem] truncate" title={l.descripcion}>{l.descripcion}</td>
                    <td className="px-3 py-2 text-center text-base font-bold text-slate-900 dark:text-white">{l.unidades}</td>
                    <td className="px-3 py-2"><PedidoCliente l={l} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {(Object.keys(RESULTADO_ESTILO) as Resultado[]).map((r) => (
                          <button key={r} onClick={(ev) => { ev.stopPropagation(); setSel(i); marcar.mutate({ l, resultado: r }) }}
                            title={`${RESULTADO_ESTILO[r].largo || RESULTADO_ESTILO[r].texto} (tecla ${RESULTADO_ESTILO[r].tecla})`}
                            aria-pressed={l.resultado === r}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${l.resultado === r ? RESULTADO_ESTILO[r].cls : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'}`}>
                            <span className="text-[11px] opacity-70">{RESULTADO_ESTILO[r].tecla}</span> {RESULTADO_ESTILO[r].texto}
                          </button>
                        ))}
                        <button onClick={(ev) => { ev.stopPropagation(); setSel(i); setNotaAbierta(l.linea); setTimeout(() => notaRef.current?.focus(), 0) }} className={`px-2 py-1.5 rounded-lg text-xs hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${l.nota ? 'text-slate-700 dark:text-slate-200 max-w-[10rem] truncate' : 'text-slate-500 whitespace-nowrap'}`} title={l.nota ? `Nota: ${l.nota} (tecla N)` : 'Nota (tecla N)'}>
                          {l.nota ? `Nota: ${l.nota}` : 'N · nota'}
                        </button>
                      </div>
                      {l.at && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{nombreCorto(l.usuario_nombre || l.usuario)} · {fmtFecha(l.at)}</div>}
                      {notaAbierta === l.linea && (
                        <form onSubmit={(ev) => { ev.preventDefault(); setNotaAbierta(null); if (l.resultado) marcar.mutate({ l, resultado: l.resultado, nota: notas[l.linea] ?? '' }) }} className="mt-1.5 flex gap-1" onClick={(ev) => ev.stopPropagation()}>
                          <input ref={notaRef} value={notas[l.linea] ?? l.nota ?? ''} onChange={(ev) => setNotas({ ...notas, [l.linea]: ev.target.value })}
                            onKeyDown={(ev) => { if (ev.key === 'Escape') setNotaAbierta(null) }}
                            placeholder="p. ej. viene otra referencia, caja golpeada…" className="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                          <button type="submit" className="px-2 py-1 text-xs rounded bg-slate-700 text-white">{l.resultado ? 'Guardar' : 'Vale'}</button>
                        </form>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <Keyboard className="w-4 h-4" />
        <span><kbd className="px-1 border rounded">↑</kbd>/<kbd className="px-1 border rounded">↓</kbd> moverse</span>
        <span><kbd className="px-1 border rounded">1</kbd> OK</span><span><kbd className="px-1 border rounded">2</kbd> falta</span>
        <span><kbd className="px-1 border rounded">3</kbd> dañado / incorrecto</span><span><kbd className="px-1 border rounded">N</kbd> nota</span>
        <span><kbd className="px-1 border rounded">B</kbd> lector</span><span><kbd className="px-1 border rounded">T</kbd> terminar y avisar a comercial</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> se guarda al momento, con tu usuario y la hora</span>
      </div>

      {/* Resto del pedido (referencia) */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <button onClick={() => setVerTodo((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-200">
          Pedido completo ({e.lineas.length} líneas) <ChevronDown className={`w-4 h-4 transition-transform ${verTodo ? 'rotate-180' : ''}`} />
        </button>
        {verTodo && (
          <table className="w-full text-sm border-t border-slate-200 dark:border-slate-700">
            <tbody>
              {e.lineas.map(([cod, ref, des, uds], i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700/60">
                  <td className="px-3 py-1.5 font-mono">{cod}</td><td className="px-3 py-1.5 font-mono">{ref || 'SIN REFERENCIA'}</td>
                  <td className="px-3 py-1.5">{des}</td><td className="px-3 py-1.5 text-center">{uds}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
